import { z } from "zod"

/**
 * ИИ-загрузка каталога (04.10.2026): «закидываешь что угодно — прайс, каталог
 * PDF, папку с фото, текст из переписки — модель размечает, и магазин с
 * товарами появляется в системе».
 *
 * Поток задания:
 *   draft      заведено, грузятся материалы
 *   parsing    разбор в фоне: извлечение текста → разметка моделью → фото →
 *              категории → проверка
 *   review     черновики товаров и магазина готовы, человек проверяет и правит
 *   committing создание магазина/товаров в фоне
 *   done       загружено (часть позиций может быть с ошибкой — см. items)
 *   failed     разбор или загрузка сорвались целиком (см. error)
 *   cancelled  отменено
 *
 * Два пути разбора: файл по нашему шаблону (xlsx «Шаблон загрузки каталога
 * OSMIX») читается без модели — модель только подбирает категории и
 * дописывает пустое; всё остальное размечает модель. Создание товаров идёт
 * через сервис каталога — те же проверки, модерация и гейт цены, что у
 * продавца, никакого обхода.
 *
 * Сейчас раздел живёт в консоли площадки; кабинет продавца получит тот же
 * сервис с магазином из токена.
 */

export const CATALOG_IMPORT_JOB_STATUSES = [
  "draft",
  "parsing",
  "review",
  "committing",
  "done",
  "failed",
  "cancelled",
] as const
export const CatalogImportJobStatusSchema = z.enum(CATALOG_IMPORT_JOB_STATUSES)
export type CatalogImportJobStatus = z.infer<typeof CatalogImportJobStatusSchema>

export const CATALOG_IMPORT_JOB_STATUS_LABELS: Record<CatalogImportJobStatus, string> = {
  draft: "Материалы",
  parsing: "Разбор",
  review: "Проверка",
  committing: "Загрузка",
  done: "Загружено",
  failed: "Ошибка",
  cancelled: "Отменено",
}

export const CATALOG_IMPORT_FILE_KINDS = [
  "spreadsheet",
  "document",
  "pdf",
  "image",
  "text",
  "archive",
  "other",
] as const
export const CatalogImportFileKindSchema = z.enum(CATALOG_IMPORT_FILE_KINDS)
export type CatalogImportFileKind = z.infer<typeof CatalogImportFileKindSchema>

export const CATALOG_IMPORT_FILE_STATUSES = ["pending", "parsed", "skipped", "failed"] as const
export type CatalogImportFileStatus = (typeof CATALOG_IMPORT_FILE_STATUSES)[number]

export const CATALOG_IMPORT_ITEM_STATUSES = [
  "ready",
  "warning",
  "error",
  "excluded",
  "committed",
  "failed",
] as const
export const CatalogImportItemStatusSchema = z.enum(CATALOG_IMPORT_ITEM_STATUSES)
export type CatalogImportItemStatus = z.infer<typeof CatalogImportItemStatusSchema>

export const CATALOG_IMPORT_STAGES = [
  "extract",
  "structure",
  "photos",
  "categories",
  "validate",
  "commit",
] as const
export type CatalogImportStage = (typeof CATALOG_IMPORT_STAGES)[number]

export const CATALOG_IMPORT_STAGE_LABELS: Record<CatalogImportStage, string> = {
  extract: "Читаем файлы",
  structure: "Размечаем товары",
  photos: "Раскладываем фото",
  categories: "Подбираем категории",
  validate: "Проверяем",
  commit: "Загружаем в магазин",
}

/** Предел одного файла (архив с фото тоже), байт. */
export const CATALOG_IMPORT_FILE_MAX_BYTES = 100 * 1024 * 1024
/** Предел позиций в одном задании. */
export const CATALOG_IMPORT_MAX_ITEMS = 3000

// ── Куда грузим ──────────────────────────────────────────────────────────

export const CatalogImportTargetSchema = z.discriminatedUnion("kind", [
  /** В существующий магазин. */
  z.object({ kind: z.literal("existing_shop"), shopId: z.string().min(1) }),
  /**
   * Новый магазин. Владелец — продавец, которому уходит приглашение в кабинет.
   * Если задание запущено из заявки — контакты берутся из неё, кабинет
   * заводится тем же путём, что кнопка «Создать кабинет продавца».
   */
  z.object({
    kind: z.literal("new_shop"),
    applicationId: z.string().optional(),
    contact: z
      .object({
        name: z.string().trim().max(120).optional(),
        phone: z.string().trim().max(30).optional(),
        email: z.string().trim().max(160).optional(),
      })
      .optional(),
    /** Отправить продавцу приглашение (SMS + письмо) сразу после загрузки. */
    sendInvite: z.boolean().default(false),
  }),
])
export type CatalogImportTarget = z.infer<typeof CatalogImportTargetSchema>

export const CatalogImportOptionsSchema = z.object({
  /** Куда класть товары: черновики (видит только продавец) или сразу на модерацию. */
  publishMode: z.enum(["draft", "moderation"]).default("draft"),
  /** Категория для позиций, где модель не уверена. */
  defaultCategoryId: z.string().optional(),
  defaultBrand: z.string().trim().max(120).optional(),
  /** Писать описания заново (по фактам из материалов) или брать как есть. */
  rewriteDescriptions: z.boolean().default(true),
  /** Сверять фото с товарами по картинке (модель со зрением; дороже). */
  matchPhotosByVision: z.boolean().default(true),
  /** Заполнять характеристики категории (состав, сезон…) из материалов. */
  fillAttributes: z.boolean().default(true),
})
export type CatalogImportOptions = z.infer<typeof CatalogImportOptionsSchema>

// ── Черновики ────────────────────────────────────────────────────────────

/** Магазин, как его поняла модель (поля анкеты «О магазине»). */
export const CatalogImportShopDraftSchema = z.object({
  shopName: z.string().max(160).default(""),
  brandDescription: z.string().max(3000).default(""),
  mainProduct: z.string().max(300).default(""),
  city: z.string().max(120).default(""),
  /** Корневые разделы каталога, в которых торгует магазин. */
  categories: z.array(z.string().max(80)).max(20).default([]),
  foundingDate: z.string().max(40).optional(),
  socials: z
    .object({
      website: z.string().max(500).default(""),
      instagram: z.string().max(500).default(""),
      vk: z.string().max(500).default(""),
      telegram: z.string().max(500).default(""),
      youtube: z.string().max(500).default(""),
      rutube: z.string().max(500).default(""),
      tiktok: z.string().max(500).default(""),
      other: z.string().max(500).default(""),
    })
    .default({}),
  /** Файл задания (картинка) — логотип / обложка. */
  logoFileId: z.string().nullable().optional(),
  coverFileId: z.string().nullable().optional(),
  legal: z
    .object({
      inn: z.string().max(12).optional(),
      fullName: z.string().max(500).optional(),
      ogrn: z.string().max(15).optional(),
      address: z.string().max(500).optional(),
    })
    .optional(),
  contact: z
    .object({
      name: z.string().max(120).optional(),
      phone: z.string().max(30).optional(),
      email: z.string().max(160).optional(),
    })
    .optional(),
})
export type CatalogImportShopDraft = z.infer<typeof CatalogImportShopDraftSchema>

export const CatalogImportVariantDraftSchema = z.object({
  /** Размер производителя; пусто — безразмерный товар. */
  size: z.string().max(60).default(""),
  russianSize: z.string().max(60).optional(),
  color: z.string().max(80).default(""),
  /** Цена продажи, руб. null — не нашли (ошибка позиции). */
  price: z.number().nonnegative().nullable(),
  oldPrice: z.number().nonnegative().nullable().optional(),
  stock: z.number().int().nonnegative().nullable().optional(),
  barcode: z.string().max(60).optional(),
})
export type CatalogImportVariantDraft = z.infer<typeof CatalogImportVariantDraftSchema>

export const CatalogImportItemDraftSchema = z.object({
  name: z.string().max(300),
  /** Артикул продавца; пусто — шлюз сгенерирует при загрузке. */
  article: z.string().max(120).default(""),
  brand: z.string().max(120).default(""),
  /** Лист дерева категорий v2; null — не определили. */
  categoryId: z.string().nullable(),
  /** Путь категории человеческим текстом («Женщинам / Одежда / Платья») — для показа. */
  categoryPath: z.string().max(500).optional(),
  /** Как категория названа в материалах — подсказка, если categoryId пуст. */
  categoryHint: z.string().max(300).optional(),
  color: z.string().max(80).default(""),
  description: z.string().max(2000).default(""),
  composition: z.string().max(500).optional(),
  /** Свободные характеристики «название → значение». */
  characteristics: z.record(z.string().max(500)).default({}),
  /** Характеристики схемы категории (ключи из схемы). */
  categoryAttributes: z.record(z.unknown()).optional(),
  tags: z.array(z.string().max(40)).max(20).default([]),
  keywords: z.array(z.string().max(80)).max(30).default([]),
  variants: z.array(CatalogImportVariantDraftSchema).max(200),
  /** Фото из файлов задания (id картинок), по порядку; первое — главное. */
  photoFileIds: z.array(z.string()).max(30).default([]),
  /** Фото по внешним ссылкам из материалов — шлюз скачает при загрузке. */
  photoUrls: z.array(z.string().max(2000)).max(30).default([]),
  dimensions: z
    .object({
      lengthCm: z.number().nonnegative().optional(),
      widthCm: z.number().nonnegative().optional(),
      heightCm: z.number().nonnegative().optional(),
      weightG: z.number().nonnegative().optional(),
    })
    .optional(),
  madeToOrder: z.boolean().optional(),
  madeToOrderLeadTimeDays: z.number().int().nonnegative().optional(),
  /** Минимальный заказ, шт. (колонка шаблона «Мин. заказ, шт.»). */
  minOrderQty: z.number().int().positive().optional(),
  /** Откуда позиция: файл и место в нём («лист Товары, строка 12», «стр. 3»). */
  sourceRefs: z
    .array(z.object({ fileId: z.string(), locator: z.string().max(200).optional() }))
    .max(20)
    .default([]),
  /** Уверенность модели 0…1 — позиции ниже 0,6 подсвечиваются. */
  confidence: z.number().min(0).max(1).default(1),
})
export type CatalogImportItemDraft = z.infer<typeof CatalogImportItemDraftSchema>

export type CatalogImportIssue = {
  /** Поле черновика (`name`, `variants.0.price`, `photoFileIds`…); пусто — к позиции целиком. */
  field?: string
  level: "error" | "warning"
  message: string
}

export type CatalogImportItem = {
  id: string
  jobId: string
  position: number
  status: CatalogImportItemStatus
  data: CatalogImportItemDraft
  issues: CatalogImportIssue[]
  /** Товар каталога после загрузки. */
  productId: string | null
  /** Уже заведённый товар магазина с тем же артикулом — загрузка его обновит. */
  existingProductId: string | null
  updatedAt: string
}

export type CatalogImportFile = {
  id: string
  jobId: string
  /** Файл из архива — ссылка на архив. */
  parentId: string | null
  filename: string
  /** Путь внутри архива («Платья/синее/1.jpg») — по папкам раскладываются фото. */
  path: string
  mime: string
  sizeBytes: number
  kind: CatalogImportFileKind
  status: CatalogImportFileStatus
  /** Почему пропущен / что не так («зашифрованный PDF», «пустой лист»). */
  note: string | null
  isImage: boolean
  /** Сколько позиций ссылаются на файл (фото или источник). */
  usedByItems: number
  /** Путь шлюза для просмотра (с токеном консоли). */
  contentPath: string
  createdAt: string
}

export type CatalogImportUsage = {
  calls: number
  tokensIn: number
  tokensOut: number
  /** Стоимость по данным OpenRouter, $. */
  costUsd: number
}

export type CatalogImportProgress = {
  stage: CatalogImportStage | null
  done: number
  total: number
  message: string
}

export type CatalogImportStats = {
  files: number
  images: number
  items: number
  ready: number
  warnings: number
  errors: number
  excluded: number
  committed: number
  failed: number
}

export type CatalogImportJob = {
  id: string
  title: string
  status: CatalogImportJobStatus
  target: CatalogImportTarget
  options: CatalogImportOptions
  /** Подсказка человека модели: «это летняя коллекция, цены оптовые ×2». */
  instructions: string
  model: string
  /** Как разобрано: по шаблону без модели, моделью, или смешанно. */
  mode: "template" | "ai" | "mixed" | null
  /**
   * Магазин назначения: существующий или созданный загрузкой. `id` — ключ
   * магазина (users.shop_id, у продавца без витрины — id продавца);
   * `sellerUserId` — продавец-владелец, когда он известен (ссылка на карточку
   * продавца в консоли).
   */
  shop: { id: string; name: string; sellerUserId?: string } | null
  application: { id: string; shopName: string } | null
  shopDraft: CatalogImportShopDraft | null
  progress: CatalogImportProgress
  stats: CatalogImportStats
  usage: CatalogImportUsage
  error: string | null
  createdBy: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
  committedAt: string | null
}

export type CatalogImportJobDetail = CatalogImportJob & {
  files: CatalogImportFile[]
  items: CatalogImportItem[]
}

// ── Запросы ──────────────────────────────────────────────────────────────

export const CatalogImportJobCreateSchema = z.object({
  title: z.string().trim().max(200).optional(),
  target: CatalogImportTargetSchema,
  instructions: z.string().max(4000).optional(),
  model: z.string().trim().max(120).optional(),
  options: CatalogImportOptionsSchema.partial().optional(),
})
export type CatalogImportJobCreate = z.infer<typeof CatalogImportJobCreateSchema>

export const CatalogImportJobPatchSchema = z.object({
  title: z.string().trim().max(200).optional(),
  target: CatalogImportTargetSchema.optional(),
  instructions: z.string().max(4000).optional(),
  model: z.string().trim().max(120).optional(),
  options: CatalogImportOptionsSchema.partial().optional(),
  shopDraft: CatalogImportShopDraftSchema.partial().optional(),
})
export type CatalogImportJobPatch = z.infer<typeof CatalogImportJobPatchSchema>

export const CatalogImportTextInputSchema = z.object({
  text: z.string().trim().min(1).max(500_000),
  filename: z.string().trim().max(200).optional(),
})
export type CatalogImportTextInput = z.infer<typeof CatalogImportTextInputSchema>

/** Взять файлы из карточки заявки (прайсы, фото, которые прислал продавец). */
export const CatalogImportFromApplicationSchema = z.object({
  /** Пусто — все файлы заявки. */
  fileIds: z.array(z.string()).max(500).optional(),
})
export type CatalogImportFromApplication = z.infer<typeof CatalogImportFromApplicationSchema>

export const CatalogImportItemPatchSchema = z.object({
  data: CatalogImportItemDraftSchema.partial().optional(),
  /** Исключить из загрузки / вернуть. */
  excluded: z.boolean().optional(),
})
export type CatalogImportItemPatch = z.infer<typeof CatalogImportItemPatchSchema>

export const CatalogImportBulkPatchSchema = z.object({
  itemIds: z.array(z.string()).min(1).max(CATALOG_IMPORT_MAX_ITEMS),
  set: z.object({
    categoryId: z.string().nullable().optional(),
    brand: z.string().max(120).optional(),
    excluded: z.boolean().optional(),
    /** Наценка к цене в процентах (−90…+500) — оптовый прайс в розницу. */
    priceMarkupPercent: z.number().min(-90).max(500).optional(),
  }),
})
export type CatalogImportBulkPatch = z.infer<typeof CatalogImportBulkPatchSchema>

export const CatalogImportRegenerateSchema = z.object({
  /** Что переписать: описание, ключевые слова, характеристики. */
  fields: z.array(z.enum(["description", "keywords", "characteristics", "category"])).min(1),
  /** Пожелание человека («короче», «без слова премиум»). */
  instruction: z.string().max(1000).optional(),
})
export type CatalogImportRegenerate = z.infer<typeof CatalogImportRegenerateSchema>

export const CatalogImportCommitSchema = z.object({
  /** Пусто — все позиции в статусах ready и warning. */
  itemIds: z.array(z.string()).max(CATALOG_IMPORT_MAX_ITEMS).optional(),
})
export type CatalogImportCommit = z.infer<typeof CatalogImportCommitSchema>

export type CatalogImportModel = {
  id: string
  label: string
  /** Понимает картинки и PDF — нужен для фото и сканов. */
  vision: boolean
  /** Цена за 1 млн токенов, $. */
  priceIn: number
  priceOut: number
  note?: string
}

export type CatalogImportModels = { models: CatalogImportModel[]; default: string; enabled: boolean }
